/**
 * PATH: packages/bills/src/components/BillPaymentSection.tsx
 * FILE_ID: BILLS-PKG-CMP-PAYMENT-001
 * PURPOSE: Right-rail "Payment" section — supplier payment term + the
 *          term-derived due date + mark-as-paid + bank-feed match.
 *
 * WHY: Operators wanted the due date to come from the SUPPLIER's internal
 *      term (CoD / 7 / 30 / 45 / 60 / 90 / EOM / Custom), not just whatever
 *      the AI read off the PDF, and to mark a bill "already paid" with a
 *      future bank-feed search. Extracted out of BillDataPanel so that file
 *      stays focused on field-editing and this owns the payment surface.
 *
 * FLOW:
 *   supplier term select ─→ onSetSupplierTerm({term_type,custom_days})
 *                              └─ backend writes Supplier + re-derives due_date
 *   "Mark as paid" form  ─→ onMarkPaid({paid:true, paid_at, amount, …})
 *   "Search bank feeds"  ─→ client.getBillPaymentSuggestions (stub today)
 *
 * DEPENDENCIES:
 *   - useBillsClient: availability checks + the read-only suggestions fetch.
 *   - ../paymentTerms: the term option list + label fallback.
 *   - ./_payments METHOD_LABELS + ./_primitives Field/input/btn styles.
 */
import { useMemo, useState } from 'react';
import { useBillsClient } from '../client';
import {
  EXEMPT_TERM_OPTION,
  SUPPLIER_TERM_OPTIONS,
  termSourceNote,
  termTypeLabel,
} from '../paymentTerms';
import type {
  BillDetail,
  BillPaymentSuggestion,
  BillTermType,
  MarkBillPaidInput,
  SupplierTermType,
} from '../types';
import { METHOD_LABELS } from './_payments';
import { Field, inputCls, btnPrimary, btnSecondary, btnGhost } from './_primitives';
import { Section } from './BillFieldPrimitives';

interface Props {
  bill: BillDetail;
  busy: boolean;
  /**
   * Apply (or clear) a payment term at the chosen scope. At supplier scope
   * the parent auto-creates + links a supplier from the vendor when none is
   * linked yet, so the chips are always actionable. ``''`` clears the term
   * at that scope — at bill scope that means "go back to inheriting".
   */
  onApplyTerm: (
    termType: BillTermType | '',
    customDays?: number | null,
    scope?: 'bill' | 'supplier',
  ) => Promise<void>;
  onMarkPaid: (input: MarkBillPaidInput) => Promise<void>;
  /** Extracted vendor name — shown in the "will create" hint when unlinked. */
  vendorName: string;
}

/** The term we suggest when a supplier has none yet (most common in AU AP). */
const RECOMMENDED_TERM: SupplierTermType = 'net_30';

/** Which level a term write lands on. */
type TermScope = 'bill' | 'supplier';

const SCOPE_OPTIONS: readonly { value: TermScope; label: string; hint: string }[] = [
  {
    value: 'supplier',
    label: 'This supplier',
    hint: "Sets the vendor's term and re-dates all their unpaid bills.",
  },
  {
    value: 'bill',
    label: 'This bill only',
    hint: "A one-off exception; the vendor's own term is left alone.",
  },
] as const;

export function BillPaymentSection({ bill, busy, onApplyTerm, onMarkPaid, vendorName }: Props) {
  const client = useBillsClient();
  const canSetTerm = typeof client.setSupplierTerm === 'function';
  const canMarkPaid = typeof client.markPaid === 'function';
  const editable = bill.status !== 'pushed';

  return (
    <Section title="Payment">
      {canSetTerm && (
        <SupplierTermControl
          bill={bill}
          busy={busy}
          disabled={busy || !editable}
          onApplyTerm={onApplyTerm}
          vendorName={vendorName}
        />
      )}

      {canMarkPaid && (
        <PaidControl bill={bill} busy={busy} onMarkPaid={onMarkPaid} />
      )}

      <BankFeedMatch billId={bill.id} />
    </Section>
  );
}

/* ───────────────────── supplier payment term ───────────────────── */

function SupplierTermControl({
  bill,
  busy,
  disabled,
  onApplyTerm,
  vendorName,
}: {
  bill: BillDetail;
  busy: boolean;
  disabled: boolean;
  onApplyTerm: (
    termType: BillTermType | '',
    customDays?: number | null,
    scope?: TermScope,
  ) => Promise<void>;
  vendorName: string;
}) {
  const supplier = bill.supplier_detail;
  const supplierLinked = Boolean(supplier?.id);
  const source = bill.payment_term_source ?? 'none';
  // Which level the operator is editing. Default to whichever already owns
  // the term, so re-picking a chip doesn't silently change the blast radius.
  const [scope, setScope] = useState<TermScope>(
    source === 'bill' || source === 'exempt' ? 'bill' : 'supplier',
  );
  const billTerm = (bill.payment_term_type ?? '') as BillTermType | '';
  const supplierTerm = (supplier?.payment_term_type ?? '') as SupplierTermType | '';
  // The chips reflect the level being edited, not the resolved term:
  // at bill scope a blank bill means "inheriting", so nothing is active.
  const currentType = scope === 'bill' ? billTerm : supplierTerm;
  // Local "custom days" buffer — only submitted when the operator clicks Apply.
  const [customDays, setCustomDays] = useState<string>(() => {
    const days = scope === 'bill' ? bill.payment_term_days : supplier?.payment_term_days;
    return days != null ? String(days) : '30';
  });
  const [showCustom, setShowCustom] = useState(currentType === 'custom');

  const label =
    bill.payment_term_label ||
    termTypeLabel(supplierTerm || null, supplier?.payment_term_days);
  const sourceNote = termSourceNote(source);

  function pickTerm(value: BillTermType) {
    if (value === 'custom') {
      setShowCustom(true);
      return;
    }
    setShowCustom(false);
    void onApplyTerm(value, null, scope);
  }

  function applyCustom() {
    const n = parseInt(customDays, 10);
    if (!Number.isFinite(n) || n < 0) return;
    void onApplyTerm('custom', n, scope);
  }

  return (
    <div className="space-y-1.5 rounded-md border border-border bg-background p-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium text-muted-foreground">
          Payment term
        </span>
        {currentType && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              setShowCustom(false);
              void onApplyTerm('', null, scope);
            }}
            className="text-[11px] text-muted-foreground hover:text-destructive disabled:opacity-40"
            title={
              scope === 'bill'
                ? "Clear this bill's exception and go back to the supplier's term"
                : "Clear the supplier's payment term"
            }
          >
            × Clear
          </button>
        )}
      </div>

      {/* Whose term am I setting? The two choices have very different blast
          radii, so the operator picks before the chips do anything. */}
      <div className="flex gap-1" role="radiogroup" aria-label="Apply term to">
        {SCOPE_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={scope === o.value}
            disabled={disabled}
            onClick={() => setScope(o.value)}
            title={o.hint}
            className={[
              'flex-1 rounded border px-2 py-1 text-[11px] font-medium transition-colors disabled:opacity-50',
              scope === o.value
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-background text-muted-foreground hover:bg-accent',
            ].join(' ')}
          >
            {o.label}
          </button>
        ))}
      </div>

      {/* Clickable chips — always actionable. When no supplier is linked yet,
          picking a term creates + links one from the vendor first. */}
      <div className="flex flex-wrap gap-1">
        {SUPPLIER_TERM_OPTIONS.map((o) => {
          const active = currentType === o.value || (o.value === 'custom' && showCustom);
          const recommended = !currentType && o.value === RECOMMENDED_TERM;
          return (
            <button
              key={o.value}
              type="button"
              disabled={disabled}
              onClick={() => pickTerm(o.value)}
              title={o.label}
              className={[
                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium transition-colors disabled:opacity-50',
                active
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-background text-foreground hover:bg-accent',
              ].join(' ')}
            >
              {o.shortLabel}
              {recommended && (
                <span className="rounded-full bg-amber-500/15 px-1 text-[9px] font-semibold uppercase text-amber-600 dark:text-amber-400">
                  rec
                </span>
              )}
            </button>
          );
        })}
        {/* Exempting is a statement about ONE invoice, never the vendor. */}
        {scope === 'bill' && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => pickTerm(EXEMPT_TERM_OPTION.value)}
            title={EXEMPT_TERM_OPTION.label}
            className={[
              'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium transition-colors disabled:opacity-50',
              billTerm === 'exempt'
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-dashed border-border bg-background text-muted-foreground hover:bg-accent',
            ].join(' ')}
          >
            {EXEMPT_TERM_OPTION.shortLabel}
          </button>
        )}
      </div>

      {showCustom && (
        <div className="flex items-end gap-2">
          <Field label="Custom days">
            <input
              type="number"
              min={0}
              inputMode="numeric"
              className={inputCls}
              value={customDays}
              disabled={disabled}
              onChange={(e) => setCustomDays(e.target.value)}
            />
          </Field>
          <button
            type="button"
            className={btnSecondary}
            disabled={disabled}
            onClick={applyCustom}
          >
            Apply
          </button>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground">
        {label ? (
          <>
            Term: <span className="font-medium text-foreground">{label}</span>
            {sourceNote && <> ({sourceNote})</>}
            {bill.predicted_due_date && (
              <> · predicts due {fmtDate(bill.predicted_due_date)}</>
            )}
          </>
        ) : supplierLinked ? (
          'No term set on this supplier yet — pick one to predict the due date.'
        ) : vendorName.trim() ? (
          `Pick a term — “${vendorName.trim()}” will be created as a supplier and linked.`
        ) : (
          'Add a vendor name above, then pick a term to create + link the supplier.'
        )}
      </p>
      {scope === 'supplier' && supplierLinked && (
        <p className="text-[11px] text-muted-foreground">
          Also re-dates every unpaid bill from this supplier.
        </p>
      )}
      {busy && (
        <p className="text-[11px] italic text-muted-foreground">Saving…</p>
      )}
    </div>
  );
}

/* ───────────────────── mark as paid ───────────────────── */

function PaidControl({
  bill,
  busy,
  onMarkPaid,
}: {
  bill: BillDetail;
  busy: boolean;
  onMarkPaid: (input: MarkBillPaidInput) => Promise<void>;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const isPaid = bill.payment_status === 'paid';

  if (isPaid) {
    return (
      <div className="space-y-1 rounded-md border border-emerald-300 bg-emerald-50 p-2 text-[11px] dark:border-emerald-900 dark:bg-emerald-950/40">
        <div className="flex items-center justify-between">
          <span className="font-medium text-emerald-800 dark:text-emerald-200">
            ✓ Paid{bill.paid_at ? ` on ${fmtDate(bill.paid_at)}` : ''}
          </span>
          <button
            type="button"
            className={btnGhost}
            disabled={busy}
            onClick={() => void onMarkPaid({ paid: false })}
            title="Clear the paid state"
          >
            Mark as unpaid
          </button>
        </div>
        <p className="tabular-nums text-emerald-900/90 dark:text-emerald-200/90">
          {bill.paid_amount ?? bill.total ?? '—'} {bill.currency}
          {bill.payment_method ? ` · ${methodLabel(bill.payment_method)}` : ''}
        </p>
        {bill.payment_reference && (
          <p className="truncate text-emerald-900/80 dark:text-emerald-200/80">
            Ref: {bill.payment_reference}
          </p>
        )}
        {bill.paid_matched_bank_ref && (
          <p className="truncate text-emerald-900/80 dark:text-emerald-200/80">
            ↔ Bank feed: {bill.paid_matched_bank_ref}
          </p>
        )}
      </div>
    );
  }

  if (!formOpen) {
    return (
      <button
        type="button"
        className={btnSecondary}
        disabled={busy}
        onClick={() => setFormOpen(true)}
      >
        Mark as already paid
      </button>
    );
  }

  return (
    <MarkPaidForm
      bill={bill}
      busy={busy}
      onSubmit={async (input) => {
        await onMarkPaid(input);
        setFormOpen(false);
      }}
      onCancel={() => setFormOpen(false)}
    />
  );
}

function MarkPaidForm({
  bill,
  busy,
  onSubmit,
  onCancel,
}: {
  bill: BillDetail;
  busy: boolean;
  onSubmit: (input: MarkBillPaidInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(bill.total ?? '');
  const [method, setMethod] = useState('eft');
  const [reference, setReference] = useState('');

  const methods = useMemo(
    () => Object.keys(METHOD_LABELS) as Array<keyof typeof METHOD_LABELS>,
    [],
  );

  return (
    <form
      className="space-y-2 rounded-md border border-border bg-background p-2"
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit({
          paid: true,
          paid_at: paidAt,
          amount: amount === '' ? null : amount,
          method,
          reference,
        });
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <Field label="Paid on">
          <input
            type="date"
            className={inputCls}
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            required
          />
        </Field>
        <Field label="Amount">
          <input
            inputMode="decimal"
            className={inputCls + ' tabular-nums'}
            value={amount}
            placeholder={bill.total ?? '0.00'}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
        <Field label="Method">
          <select
            className={inputCls}
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          >
            {methods.map((m) => (
              <option key={m} value={m}>
                {METHOD_LABELS[m]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Reference">
          <input
            className={inputCls}
            value={reference}
            placeholder="EFT ref / cheque #"
            onChange={(e) => setReference(e.target.value)}
          />
        </Field>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button type="button" className={btnGhost} onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className={btnPrimary} disabled={busy}>
          {busy ? 'Saving…' : 'Mark paid'}
        </button>
      </div>
    </form>
  );
}

/* ───────────────────── bank-feed match ───────────────────── */

function BankFeedMatch({ billId }: { billId: string }) {
  const client = useBillsClient();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<BillPaymentSuggestion[] | null>(null);
  const [connected, setConnected] = useState<boolean | null>(null);
  const [message, setMessage] = useState<string>('');

  if (typeof client.getBillPaymentSuggestions !== 'function') return null;

  async function search() {
    setOpen(true);
    setLoading(true);
    setError(null);
    try {
      const r = await client.getBillPaymentSuggestions!(billId);
      setSuggestions(r.suggestions);
      setConnected(r.bank_feed_connected);
      setMessage(r.message);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bank-feed search failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-1.5">
      <button type="button" className={btnSecondary} disabled={loading} onClick={search}>
        {loading ? 'Searching…' : 'Search bank feeds'}
      </button>
      {open && !loading && (
        <div className="rounded-md border border-border bg-muted/20 p-2 text-[11px]">
          {error ? (
            <p className="text-destructive">{error}</p>
          ) : suggestions && suggestions.length > 0 ? (
            <ul className="space-y-1">
              {suggestions.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between gap-2 tabular-nums"
                >
                  <span className="truncate">
                    {fmtDate(s.date)} · {s.description || s.reference || '—'}
                  </span>
                  <span className="font-medium">{s.amount}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground">
              {message ||
                (connected === false
                  ? 'No bank feed connected yet — connect one to match payments automatically.'
                  : 'No matching bank transactions found.')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ───────────────────── helpers ───────────────────── */

function methodLabel(method: string): string {
  const known = METHOD_LABELS as Record<string, string>;
  return known[method] ?? method;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
